import { defineStore } from 'pinia';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { BankDetailsEndPoint } from '@/modules/common/enums/end-point.ts';
import type { CounterpartyBankDetails } from '@/modules/counterparty/types/counterparty-bank-details.type.ts';
import type { CounterpartyListRequest } from '@/modules/counterparty/types/counterparty-list-request.type.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

type RawBankDetailsResponse = { data: ItemsDto<CounterpartyBankDetails> };

function getList(params: CounterpartyListRequest, signal: AbortSignal) {
  return GatewayApi.execute<RawBankDetailsResponse>(BankDetailsEndPoint.Get, {
    filter: GatewayFilter.and(GatewayFilter.eq('CLIENT_ID', params.clientId)),
    limit: params.limit,
    offset: params.offset,
    signal,
    mapData: false,
  });
}

type BankDetailsState = {
  items: CounterpartyBankDetails[];
  count: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  controller: AbortController | null;
};

export const useBankDetailsStore = defineStore(StoreId.BankDetails, {
  state: (): BankDetailsState => ({
    items: [],
    count: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    controller: null,
  }),
  getters: {
    list: (state) => state.items,
  },
  actions: {
    async fetch(params: CounterpartyListRequest) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;
      this.isLoadingMore = false;

      try {
        const response = await getList(params, controller.signal);

        if (this.controller !== controller) {
          return;
        }

        this.items = [...response.data.items];
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchMore(params: CounterpartyListRequest) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoadingMore = true;
      this.isLoading = false;

      try {
        const response = await getList(params, controller.signal);

        if (this.controller !== controller) {
          return;
        }

        this.items = [...this.items, ...response.data.items];
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoadingMore = false;
          this.controller = null;
        }
      }
    },
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.isLoadingMore = false;
      this.controller = null;
    },
    clear() {
      this.abort();
      this.items = [];
      this.count = null;
      this.error = null;
      this.isLoadingMore = false;
    },
  },
});
